import { memo, useState, useRef, useEffect, useCallback } from "react";
import type { FileItem } from "../../types";
import { ImageBoundingBoxes } from "./ImageBoundingBoxes";

interface LargeThumbnailDimensions {
  width: number | null;
  height: number | null;
}

interface ImageDialogViewerProps {
  file: FileItem;
  imageUrl: string | null;
  isLoading: boolean;
  imageError: boolean;
  zoom: number;
  pan: { x: number; y: number };
  isDragging: boolean;
  imageContainerRef: React.RefObject<HTMLDivElement | null>;
  onMouseDown: (e: React.MouseEvent) => void;
  onResetZoom: () => void;
  minConfidence?: number | null;
  largeThumbnailDimensions?: LargeThumbnailDimensions | null;
}

function ImageDialogViewerComponent({
  file,
  imageUrl,
  isLoading,
  imageError,
  zoom,
  pan,
  isDragging,
  imageContainerRef,
  onMouseDown,
  onResetZoom,
  minConfidence,
  largeThumbnailDimensions,
}: ImageDialogViewerProps) {
  const [imageDimensions, setImageDimensions] = useState<{
    width: number;
    height: number;
    displayWidth: number;
    displayHeight: number;
  } | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const updateImageDimensions = useCallback(() => {
    if (imageRef.current) {
      const naturalWidth = imageRef.current.naturalWidth;
      const naturalHeight = imageRef.current.naturalHeight;

      if (naturalWidth > 0 && naturalHeight > 0) {
        const displayWidth = imageRef.current.offsetWidth;
        const displayHeight = imageRef.current.offsetHeight;

        setImageDimensions({
          width: naturalWidth,
          height: naturalHeight,
          displayWidth,
          displayHeight,
        });
      }
    }
  }, []);

  const handleImageLoad = () => {
    updateImageDimensions();
  };

  useEffect(() => {
    if (imageUrl && !isLoading) {
      setImageDimensions(null);
    }
  }, [imageUrl, isLoading]);

  useEffect(() => {
    if (imageDimensions && imageRef.current) {
      const timeoutId = setTimeout(() => {
        updateImageDimensions();
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [imageDimensions, updateImageDimensions]);

  useEffect(() => {
    if (!imageRef.current || !imageDimensions) return;

    const resizeObserver = new ResizeObserver(() => {
      updateImageDimensions();
    });

    resizeObserver.observe(imageRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [imageDimensions, updateImageDimensions]);

  const hasClassifications =
    file.classifications && file.classifications.length > 0;

  return (
    <div
      ref={imageContainerRef}
      className="absolute inset-0 flex items-center justify-center overflow-hidden"
      onMouseDown={onMouseDown}
      style={{
        cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : "default",
      }}
    >
      {isLoading && (
        <div className="text-white text-center">
          <p>Loading image...</p>
        </div>
      )}
      {!isLoading && imageUrl && !imageError && (
        <div
          ref={wrapperRef}
          className="relative inline-block"
          style={{
            overflow: "visible",
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transition: isDragging ? "none" : "transform 0.2s",
          }}
        >
          <img
            ref={imageRef}
            src={imageUrl}
            alt={file.name}
            className="object-contain select-none"
            style={{
              maxWidth: "100vw",
              maxHeight: "100vh",
              width: "auto",
              height: "auto",
              display: "block",
            }}
            onLoad={handleImageLoad}
            onDoubleClick={onResetZoom}
            draggable={false}
          />
          {hasClassifications &&
            imageDimensions &&
            imageDimensions.width > 0 &&
            imageDimensions.height > 0 && (
              <ImageBoundingBoxes
                classifications={file.classifications!}
                imageWidth={imageDimensions.width}
                imageHeight={imageDimensions.height}
                displayWidth={imageDimensions.displayWidth}
                displayHeight={imageDimensions.displayHeight}
                largeThumbnailDimensions={largeThumbnailDimensions}
                minConfidence={minConfidence}
              />
            )}
        </div>
      )}
      {!isLoading && imageError && (
        <div className="text-white text-center">
          <p>Failed to load image</p>
        </div>
      )}
    </div>
  );
}

export const ImageDialogViewer = memo(ImageDialogViewerComponent);
